import * as React from 'react'
import { useState, useEffect } from 'react'
import {
    Table,
    Thead,
    Tr,
    Th,
    Tbody,
    Td,
    ThProps
} from '@patternfly/react-table';
import {
    useHistory,
    Link,
    useRouteMatch
} from "react-router-dom";

import {
    Button,
    Toolbar,
    ToolbarItem,
    ToolbarContent,
    PaginationVariant,
    Pagination,
} from '@patternfly/react-core';
import { SearchIcon } from '@patternfly/react-icons'

import { ChangeRequest } from '../../../../../shared/secretmanager/changerequests/types';
import {
    ResourceItem,
    ResourceItemType,
} from '@app/components/ResourceItem'

import { Filter } from '../components/Filter'
import { applyFilter } from '@app/utils/parseSearchString';
import Timestamp from 'react-timestamp';
import { useChangeRequestsList } from '../../hooks/useSecretManagerData';
import { jiraUrlFor } from '../../../../../shared/secretmanager/changerequests/jira';


export const ChangeRequestsList: React.FunctionComponent<{ change_requests: ChangeRequest[]; clusterSelector?: React.ReactNode }> = ({ change_requests, clusterSelector }) => {

    const { url } = useRouteMatch<{ path: string, url: string }>();

    const [managedRequests, setManagedRequests] = useState<ChangeRequest[]>(change_requests);
    const [perPage, setPerPage] = React.useState<number>(20);
    const [paginatedRows, setPaginatedRows] = useState<ChangeRequest[]>(change_requests.slice(0, perPage));
    const [page, setPage] = React.useState<number>(1);

    const [activeSortIndex, setActiveSortIndex] = React.useState<number | undefined>(0);
    const [activeSortDirection, setActiveSortDirection] = React.useState<'asc' | 'desc' | undefined>(undefined);
    const getSortableRowValues = (request: ChangeRequest): (string | number | Date | undefined)[] => {
        const { id, ticket, author, date, state } = request;
        return [id, ticket, author, date, state];
    };

    const triggerSort = (index, direction) => {
        if (activeSortIndex !== undefined) {
            let sorted = managedRequests.sort((a, b) => {
                const aValue = getSortableRowValues(a)[index];
                const bValue = getSortableRowValues(b)[index];
                if (typeof aValue === 'number') {
                    // Numeric sort
                    if (direction === 'asc') {
                        return (aValue as number) - (bValue as number);
                    }
                    return (bValue as number) - (aValue as number);
                } else {
                    if (index === 1 && typeof aValue === "string" && typeof bValue === "string") {
                        // hack for tickets :D
                        const [prefixA, numA] = aValue.split("-");
                        const [prefixB, numB] = bValue.split("-");

                        if (prefixA !== prefixB) {
                            if (direction === 'asc') {
                                return prefixA.localeCompare(prefixB); // Sort alphabetically by prefix
                            } else {
                                return prefixB.localeCompare(prefixA);
                            }
                        }
                        if (direction === 'asc') {
                            return parseInt(numA, 10) - parseInt(numB, 10);
                        } else {
                            return parseInt(numB, 10) - parseInt(numA, 10);
                        }
                        
                    } else {
                        // String sort
                        if (direction === 'asc') {
                            return (aValue as string).localeCompare(bValue as string);
                        }
                        return (bValue as string).localeCompare(aValue as string);
                    }

                }
            });

            setManagedRequests([...sorted])
            setPaginatedRows([...sorted.slice(0, perPage)]);
        }
    }


    const getSortParams = (columnIndex: number): ThProps['sort'] => ({
        sortBy: {
            index: activeSortIndex,
            direction: activeSortDirection,
            defaultDirection: 'asc' // starting sort direction when first sorting a column. Defaults to 'asc'
        },
        onSort: (_event, index, direction) => {
            setActiveSortIndex(index);
            setActiveSortDirection(direction);
            triggerSort(index, direction);
        },
        columnIndex
    });

    const column_names = {
        id: 'ID',
        ticket: 'Ticket',
        author: 'Author',
        state: 'State',
        date: 'Date'
    }

    const table = (
        <Tbody>
            {paginatedRows.map((request, row_index) => (

                <Tr
                    key={request.id}
                >
                    <Td>
                        <ResourceItem type={ResourceItemType.ChangeRequest}>
                            <Link to={`${url}/view/${request.id}`}>
                                {request.id}
                            </Link>
                        </ResourceItem>
                    </Td>
                    <Td><a target='_blank' rel='noopener noreferrer' href={jiraUrlFor(request.ticket)}>{request.ticket}</a></Td>
                    <Td>{request.author}</Td>
                    <Td><Timestamp date={request.date} /></Td>
                    <Td>{request.state}</Td>
                </Tr>
            ))
            }
        </Tbody>
    )

    const handleSetPage = (
        _evt: React.MouseEvent | React.KeyboardEvent | MouseEvent,
        newPage: number,
        _perPage: number | undefined,
        startIdx: number | undefined,
        endIdx: number | undefined
    ) => {
        setPaginatedRows(managedRequests.slice(startIdx, endIdx));
        setPage(newPage);
    };

    const handlePerPageSelect = (
        _evt: React.MouseEvent | React.KeyboardEvent | MouseEvent,
        newPerPage: number,
        _newPage: number,
        startIdx: number | undefined,
        endIdx: number | undefined
    ) => {
        setPaginatedRows(managedRequests.slice(startIdx, endIdx));
        setPerPage(newPerPage);
    };

    const renderPagination = (variant: PaginationVariant) => {

        return (
            <Pagination
                itemCount={managedRequests.length}
                page={page}
                perPage={perPage}
                onSetPage={handleSetPage}
                onPerPageSelect={handlePerPageSelect}
                variant={variant}
            />
        );
    };



    const toolbarItems = (
        <React.Fragment>
            <Toolbar id="rtl-paginated-table">
                <ToolbarContent>
                    <ToolbarItem variant="pagination">{renderPagination(PaginationVariant.bottom)}</ToolbarItem>
                </ToolbarContent>
            </Toolbar>
        </React.Fragment>
    );

    const history = useHistory();

    const handleSearch = (statement) => {
        const searchParams = new URLSearchParams(location.search);

        if (statement) {
            const filtered_requests = applyFilter(change_requests, statement);
            setManagedRequests(filtered_requests)
            setPaginatedRows(filtered_requests.slice(0, perPage));

            searchParams.set('filter', statement); // Add or update the "query" parameter
        } else {
            setManagedRequests(change_requests);
            setPaginatedRows(change_requests.slice(0, perPage));

            searchParams.delete('filter'); // Remove the parameter if the input is empty
        }

        history.replace({
            pathname: location.pathname,
            search: searchParams.toString(),
        });
    }

    const searchParams = new URLSearchParams(location.search);

    useEffect(() => {
        const filter = searchParams.get('filter');
        if (filter) {
            handleSearch(filter);
        }
    }, [])

    const filter = (
        <React.Fragment>
            <Toolbar >
                <Filter
                    onSearch={handleSearch}
                    value={searchParams.get('filter')}
                    resultsCount={managedRequests.length}
                    placeholder={"Filter for change requests containing words. To filter for specific fields use field=value (value within quotes if multi word)"}
                />
            </Toolbar>
        </React.Fragment>
    )

    const empty = (
        <Tbody>
            <Tr>
                <Td colSpan={8}>
                    <div className="dx-bullseye">
                        <div className="dx-empty-state dx-empty-state--sm">
                            <div className="dx-empty-state__icon"><SearchIcon /></div>
                            <h2 className="dx-empty-state__title">No results found</h2>
                            <div className="dx-empty-state__body">
                                No results match this filter criteria. Clear all filters and try again.
                            </div>
                            <div className="dx-empty-state__footer">
                                <Button variant="link" onClick={() => handleSearch("")}>Clear all filters</Button>
                            </div>
                        </div>
                    </div>
                </Td>
            </Tr>
        </Tbody>
    )

    return (
        <div className="secrets-page">
            <div className="secrets-page-header">
                <div className="secrets-page-header-row">
                    <h1 className="secrets-page-title">Change requests</h1>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {clusterSelector}
                        <Link to={`${url}/create`}>
                            <Button>Create change request</Button>
                        </Link>
                    </div>
                </div>
                <p className="secrets-page-subtitle">
                    Self-service tool to manage azure keyvault secrets.{' '}
                    <a className="secrets-link" target='_blank' rel='noopener noreferrer' href='https://teladochealth.atlassian.net/wiki/spaces/DEVOPS/pages/199887645/EOD+Dashboard+Secret+Manager#id-[EODDashboard]SecretManager-changerequest'>
                        User guide
                    </a>
                </p>
            </div>

            <div className="secrets-section">
                {filter}
                <div className="secrets-table-wrapper">
                    <Table aria-label="namespaces table" variant='compact' ouiaId="SortableTable">
                        <Thead>
                            <Tr>
                                <Th key={1} width={10} sort={getSortParams(0)}>{column_names.id}</Th>
                                <Th key={2} width={10} sort={getSortParams(1)}>{column_names.ticket}</Th>
                                <Th key={3} width={20} sort={getSortParams(2)}>{column_names.author}</Th>
                                <Th key={4} width={10} sort={getSortParams(3)}>{column_names.date}</Th>
                                <Th key={5} width={10} sort={getSortParams(4)}>{column_names.state}</Th>
                            </Tr>
                        </Thead>
                        {managedRequests.length ? table : empty}
                    </Table>
                </div>
                {toolbarItems}
            </div>
        </div>
    )
}

export const ChangeRequestsPage: React.FunctionComponent<{ clusterSelector?: React.ReactNode }> = ({ clusterSelector }) => {

    const { data: changeRequests, isLoading } = useChangeRequestsList();

    if (isLoading || !changeRequests) return <p>Loading...</p>

    return <ChangeRequestsList change_requests={changeRequests} clusterSelector={clusterSelector} />

}